import React, { useEffect, useState } from "react";

import { FlexibleTestingTestBlocker } from "types/graphql";
import { Header, HeaderCell, Table, Body } from "components/flexible_testing/shared/table";
import TestBlockerRow from "./blocker_row";
import { Checkbox } from "@hopper/checkbox";
import { Channel } from "stream-chat";
import { useChatContext } from "components/flexible_testing/shared/chat/chat_client_context";
import { useChat } from "components/flexible_testing/shared/chat/use_chat";

type TestBlockersProps = {
  testBlockers: FlexibleTestingTestBlocker[];
};

const TestBlockers = ({ testBlockers }: TestBlockersProps) => {
  const { isLoading: isClientLoading } = useChat();
  const { client, setTestBlockersWithChannel, testBlockersWithChannel } = useChatContext();
  const [filteredBlockers, setFilteredBlockers] = useState<FlexibleTestingTestBlocker[]>(testBlockers);
  const [hiddenStatuses, setHiddenStatuses] = useState<Array<string>>(["resolved"]);

  useEffect(() => {
    if (!isClientLoading && testBlockers && testBlockers.length > 0 && testBlockersWithChannel.size === 0) {
      const newTestBlockersWithChannel = new Map();
      const initializeChannels = async () => {
        const channelIds = testBlockers
          .flatMap(testBlocker => testBlocker?.chatChannel?.externalId)
          .filter(Boolean) as string[];

        const newChannels: Channel[] = [];
        const chunkSize = 30;
        for (let i = 0; i < channelIds.length; i += chunkSize) {
          const chunk = channelIds.slice(i, i + chunkSize);
          const channels = await retrievePrivateChannels(chunk);
          newChannels.push(...channels);
        }

        newChannels.forEach(channel => {
          const channelId = channel.cid.split(":")[1];
          if (channelId) {
            const testBlocker = testBlockers.find(blocker => blocker?.chatChannel?.externalId === channelId);
            if (testBlocker) {
              newTestBlockersWithChannel.set(testBlocker.id, channel);
            }
          }
        });

        setTestBlockersWithChannel(newTestBlockersWithChannel);
      };

      initializeChannels();
    }
  }, [testBlockers, isClientLoading]);

  useEffect(() => {
    setFilteredBlockers(filterTestBlockersByStatus(hiddenStatuses));
  }, [hiddenStatuses, testBlockers]);

  function filterTestBlockersByStatus(status: string[]) {
    return testBlockers.filter(testBlocker => !status.includes(testBlocker.status));
  }

  async function retrievePrivateChannels(channelIds: Array<string>) {
    if (channelIds.length === 0) {
      return [];
    }

    const filter = {
      type: "messaging",
      cid: { $in: channelIds.map(channelId => `messaging:${channelId}`) },
    };

    try {
      const channels = await client?.queryChannels(filter, undefined, { watch: true, limit: 30 });

      return channels || [];
    } catch {
      throw new Error("There was an unexpected issue while fetching chat channel");
    }
  }

  return (
    <div>
      <div className="my-4xl">
        <span className="p-md text-sm bg-light border-l-2 rounded border-primary pr-2xl">
          Review blockers to help testers when you can. If you need help from a test manager, <b>click ‘Get help’</b>
        </span>
      </div>
      <Checkbox
        label="Show ‘resolved’"
        onChange={() => (hiddenStatuses.includes("resolved") ? setHiddenStatuses([]) : setHiddenStatuses(["resolved"]))}
        isChecked={!hiddenStatuses.includes("resolved")}
      />
      <Table borderColor="muted" className="pt-2xl mt-2xl" data-testid="blockers-table">
        <Header className="border-b border-muted">
          <>
            <HeaderCell style={{ width: "17%" }}>TESTER</HeaderCell>
            <HeaderCell style={{ width: "40%" }}>BLOCKER</HeaderCell>
            <HeaderCell style={{ width: "9%" }}>STATUS</HeaderCell>
            <HeaderCell style={{ width: "9%" }}>REPORTED</HeaderCell>
            <HeaderCell style={{ width: "25%" }}></HeaderCell>
          </>
        </Header>
        <Body>
          {filteredBlockers.map(testBlocker => (
            <TestBlockerRow key={testBlocker._id} testBlocker={testBlocker} />
          ))}
        </Body>
      </Table>
    </div>
  );
};

export default TestBlockers;
